// src/features/tickets/services/TicketService.ts
//
// Service-Layer fuer das XTAX Support-Ticketsystem.
//
// Verantwortlichkeiten:
//   - Tickets listen / lesen / anlegen
//   - Nachrichten / interne Notizen anlegen (inkl. Frontend-Upload fuer Anhaenge,
//     wenn der Mail-Versand-Pfad nicht genommen wird)
//   - Status, Priority, Assignee, Customer aendern (mit Audit-Log)
//   - Open-Ticket-Stats fuer Navigations-Badge
//   - Gespeicherte Standardansicht der Ticketliste je Mitarbeiter lesen/speichern
//   - Manuelle Kundenzuordnung (Kunde, Firmengruppe oder Pool) inkl.
//     Absender-Regeln fuer kuenftige Mails
//
// Nicht hier:
//   - Mail-Versand → TicketMailService (ruft send-ticket-reply.js, das im
//     Backend selbst Anhaenge persistiert)
//   - Inbound-Verarbeitung → Supabase Edge Function (Stufe 2)
//
// =============================================================================
// KONVENTION: PostgREST-Embeds & Type-Sicherheit
// =============================================================================
// supabase-js typisiert PostgREST-Embeds wie `customer:customers!fk(*)` IMMER
// als Array (`{...}[]`), auch wenn die FK-Beziehung Many-to-One ist und zur
// Laufzeit ein einzelnes Object (oder null) zurueckkommt. Direkte Casts auf
// Single-Object schlagen mit TS2352 fehl.
//
// Konvention fuer alle Services im Projekt:
//
//   ARRAY-Queries mit Embeds:
//     const { data, error } = await query.returns<RawType[]>();
//
//   SINGLE-Queries mit Embeds (.maybeSingle() / .single()):
//     const { data, error } = await query.maybeSingle();
//     if (!data) return null;
//     const typed = data as unknown as RawType;
//
//   Raw-Types werden im Modul definiert, klar getrennt von View-Types.
//   Das macht den semantischen Bruch zwischen DB-Form und Application-Form
//   explizit und lokal.
//
// =============================================================================
// KONVENTION: Anhang-Upload-Pfade
// =============================================================================
// Es gibt zwei Pfade, ueber die Anhaenge in den Bucket `ticket-attachments`
// gelangen:
//
//   1. Backend-Pfad (Mailversand): send-ticket-reply.js bekommt Anhaenge als
//      base64 im Request, sendet sie als MIME-Attachment raus und persistiert
//      sie nach dem Versand in Storage + ticket_attachments. Wird genutzt fuer
//        - Ticket-Anlage mit send_email=true
//        - Reply mit is_internal_note=false und send_email=true
//
//   2. Frontend-Pfad (kein Mailversand): attachToMessage() laedt direkt ueber
//      die authentifizierte Supabase-Client-Session in Storage hoch und schreibt
//      den ticket_attachments-Eintrag. Wird genutzt fuer
//        - Interne Notizen (is_internal_note=true)
//        - Ticket-Anlage ohne Mailversand (send_email=false)
//
// Beide Pfade nutzen die gleiche Pfad-Konvention `{ticketId}/{messageId}/{filename}`
// und schreiben in dieselbe ticket_attachments-Tabelle.
// =============================================================================

import { supabase } from '../../../shared/services/supabase';
import {
  Ticket,
  TicketMessage,
  TicketListView,
  TicketDetailView,
  TicketMessageWithAttachments,
  TicketAttachment,
  TicketEvent,
  TicketActor,
  TicketStatus,
  TicketPriority,
  TicketEventType,
  TicketView,
  TicketCustomerGroupRef,
  TicketKundenKontext,
  TicketKundenVorschlag,
  TicketKundenZuordnung,
  TicketAbsenderRegel,
  CreateTicketInput,
  AddMessageInput,
  TicketListFilter,
  TicketPageFilter,
  TicketPageResult,
  TicketStats,
  TicketOperationResult,
  CreateTicketResult,
  AddMessageResult,
  TicketCustomerRef,
  TicketAttachmentInput,
  ScheduledTicketMessage,
  ScheduleReplyInput,
  ScheduleNewTicketInput,
  ScheduleResult,
  ScheduleNewTicketResult
} from '../types';
import { Customer, XtaxStaffReference } from '../../../shared/types';
import { ticketMailService } from './TicketMailService';
import type { ProofreadThreadMessage } from './TicketProofreadService';

// =============================================================================
// Raw-Types: Form, wie die DB sie liefert (Embeds als Single-Objekt | null)
// =============================================================================

/**
 * Roh-Form eines Tickets in der Liste, inkl. embedded Customer-Ref und Assignee.
 */
interface RawTicketWithJoins {
  id: string;
  ticket_number: string;
  customer_id: string | null;
  subject: string;
  status: TicketStatus;
  priority: TicketPriority;
  assigned_to_staff_id: string | null;
  source: 'intranet' | 'email' | 'system';
  gmail_thread_id: string | null;
  created_by_staff_id: string | null;
  last_message_at: string | null;
  last_message_from: 'staff' | 'customer' | 'system' | null;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
  customer_group_id?: string | null;
  customer: TicketCustomerRef | null;
  customer_group?: TicketCustomerGroupRef | null;
  assigned_to: XtaxStaffReference | null;
}

/**
 * Roh-Form eines Tickets in der Detail-Ansicht, inkl. vollem Customer.
 */
interface RawTicketDetail extends Ticket {
  customer: Customer | null;
  customer_group?: TicketCustomerGroupRef | null;
  assigned_to: XtaxStaffReference | null;
  created_by: XtaxStaffReference | null;
}

/**
 * Roh-Form einer Message mit embeddedem Author-Staff.
 */
interface RawTicketMessageWithStaff extends TicketMessage {
  author_staff: XtaxStaffReference | null;
}

/**
 * Zeile der RPC list_tickets_page: Ticket-ID in Listenreihenfolge plus
 * Gesamtzahl aller Treffer (bigint, kommt als number oder string).
 */
interface RawTicketPageRow {
  ticket_id: string;
  total_count: number | string;
}

/**
 * Zeile der RPC get_ticket_open_stats (bigint-Zaehler).
 */
interface RawTicketOpenStatsRow {
  total_open: number | string;
  mine_open: number | string;
  pool_open: number | string;
}

/**
 * Blockgroesse beim Nachladen der angereicherten Listenzeilen einer Seite.
 * Haelt die Abfrage-Adresse (.in('id', ...)) sicher unter der Laengengrenze
 * des Servers, die bei mehreren hundert IDs ueberschritten wird.
 */
const LIST_PAGE_DETAIL_CHUNK_SIZE = 100;

// =============================================================================
// Service-Klasse
// =============================================================================

class TicketService {
  // ===========================================================================
  // LIST / READ
  // ===========================================================================

  /**
   * Listet Tickets gemaess Filter. Sortierung: zuletzt aktualisierte oben.
   */
  async listTickets(filter: TicketListFilter): Promise<TicketListView[]> {
    try {
      let query = supabase
        .from('tickets')
        .select(`
          *,
          customer:customers!tickets_customer_id_fkey (
            id,
            company,
            vorname,
            nachname,
            zustaendiger_mitarbeiter_id
          ),
          assigned_to:xtax_staff!tickets_assigned_to_staff_id_fkey (
            id,
            full_name,
            initials
          )
        `)
        .order('last_message_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false })
        .limit(filter.limit ?? 100);

      // Als Duplikat markierte native Tickets ausblenden. list_hidden ist eine
      // generierte Spalte (true nur, wenn das Ticket ein markiertes Duplikat ist
      // UND seit der Markierung keine neue Aktivitaet hatte). Sie reaktiviert
      // sich automatisch: eine neue Nachricht hebt last_message_at, wodurch
      // list_hidden neu berechnet wird und das Ticket wieder erscheint. Greift
      // fuer Liste UND Suche (der Suchzweig fuegt nur .in('id', ...) hinzu).
      query = query.eq('list_hidden', false);

      // Status-Filter
      if (filter.status && filter.status.length > 0) {
        query = query.in('status', filter.status);
      }

      // Customer-Filter
      if (filter.customer_id) {
        query = query.eq('customer_id', filter.customer_id);
      }

      // Assignee-Filter (separat von view-Filter)
      if (filter.assigned_to_staff_id !== undefined) {
        if (filter.assigned_to_staff_id === null) {
          query = query.is('assigned_to_staff_id', null);
        } else {
          query = query.eq('assigned_to_staff_id', filter.assigned_to_staff_id);
        }
      }

      // Volltext-Suche: Subject + Ticket-Nummer + Nachrichtentexte + Kunden-
      // Firmenname ueber die RPC search_tickets (deutsche FTS auf Subject und
      // allen Nachrichten + ILIKE-Substring auf Subject/Nummer/Firmenname).
      // Die RPC liefert die Treffer-Ticket-IDs; die eigentliche Listen-Query
      // laedt daraus die angereicherten Zeilen, sodass Embeds, Sortierung und
      // der nachgelagerte View-Filter unveraendert bleiben. Kein Treffer ->
      // leere Liste.
      if (filter.search && filter.search.trim().length > 0) {
        const { data: matchData, error: matchError } = await supabase
          .rpc('search_tickets', { p_query: filter.search.trim() });

        if (matchError) {
          console.error('[TicketService.listTickets] Search-RPC-Error:', matchError);
          return [];
        }

        const matchedIds = ((matchData ?? []) as Array<{ ticket_id: string }>)
          .map(r => r.ticket_id);
        if (matchedIds.length === 0) {
          return [];
        }
        query = query.in('id', matchedIds);
      }

      const { data, error } = await query.returns<RawTicketWithJoins[]>();

      if (error) {
        console.error('[TicketService.listTickets] Error:', error);
        return [];
      }

      const raw = data ?? [];

      // View-Filter clientseitig anwenden (filtert ueber den geladenen Customer-JOIN)
      let filtered = raw;

      if (filter.view === 'mine') {
        const staffId = filter.current_staff_id;
        if (!staffId) {
          // Ohne staff_id koennen wir "Meine" nicht aufloesen
          return [];
        }
        filtered = raw.filter(t =>
          (t.customer && t.customer.zustaendiger_mitarbeiter_id === staffId) ||
          t.customer_id === null
        );
      } else if (filter.view === 'pool') {
        filtered = raw.filter(t => t.customer_id === null);
      }
      // view === 'all' → kein zusaetzlicher Filter

      // Nachrichten-Anzahl in einem zweiten Query holen
      const ticketIds = filtered.map(t => t.id);
      const messageCounts = await this.fetchMessageCounts(ticketIds);

      return filtered.map(t => this.mapRawToListView(t, messageCounts[t.id] ?? 0));
    } catch (error) {
      console.error('[TicketService.listTickets] Unexpected error:', error);
      return [];
    }
  }

  /**
   * Laedt eine Seite der Haupt-Ticketliste (TicketsPage).
   *
   * Ansicht, Status, Suche, Sortierung und Seitenbildung erledigt die RPC
   * list_tickets_page vollstaendig in der Datenbank; sie liefert die Ticket-IDs
   * der Seite in Listenreihenfolge plus die Gesamtzahl. Definition der
   * Ansichten (eine Quelle der Wahrheit in der DB-Funktion ticket_in_ansicht):
   *   - Meine: mir zugewiesen ODER niemandem zugewiesen und ich bin zustaendig
   *     fuer den Kunden bzw. (Gesellschaft offen) fuer die Firmengruppe
   *   - Pool:  weder Kunde noch Firmengruppe zugeordnet
   *   - Alle:  alles
   * Mit Suchbegriff werden alle Tickets inkl. geschlossener durchsucht,
   * unabhaengig von Ansicht und Statusfilter; Treffer in Betreff, Ticketnummer
   * oder Firmenname stehen vor Treffern im Nachrichtentext. Als Duplikat
   * ausgeblendete Tickets (list_hidden) erscheinen nie.
   *
   * Mit filter.customer_id (Bereich "Tickets" in den Kundenstammdaten) liefert
   * die RPC die Tickets dieses Kunden plus die Gruppentickets seiner
   * Firmengruppe, alle Status, unabhaengig von der Ansicht.
   *
   * Die angereicherten Zeilen (Kunde, Firmengruppe, Bearbeiter, Nachrichtenanzahl) werden
   * anschliessend in Bloecken geladen und in die RPC-Reihenfolge gebracht.
   * Fehler liefern success=false statt einer stillen leeren Liste.
   */
  async listTicketsPage(filter: TicketPageFilter): Promise<TicketPageResult> {
    try {
      const search = filter.search && filter.search.trim().length > 0
        ? filter.search.trim()
        : null;

      const { data: pageData, error: pageError } = await supabase
        .rpc('list_tickets_page', {
          p_view: filter.view,
          p_staff_id: filter.current_staff_id,
          p_include_closed: filter.include_closed,
          p_search: search,
          p_limit: filter.limit,
          p_offset: filter.offset,
          p_customer_id: filter.customer_id ?? null
        });

      if (pageError) {
        console.error('[TicketService.listTicketsPage] RPC-Error:', pageError);
        return { success: false, tickets: [], total_count: 0 };
      }

      const pageRows = (pageData ?? []) as RawTicketPageRow[];

      // Leere Seite: Gesamtzahl entspricht den bereits geladenen Tickets
      // (bei offset 0 also 0), damit kein weiteres Nachladen angeboten wird.
      if (pageRows.length === 0) {
        return { success: true, tickets: [], total_count: filter.offset };
      }

      const totalCount = Number(pageRows[0].total_count) || 0;
      const orderedIds = pageRows.map(r => r.ticket_id);

      const chunks: string[][] = [];
      for (let i = 0; i < orderedIds.length; i += LIST_PAGE_DETAIL_CHUNK_SIZE) {
        chunks.push(orderedIds.slice(i, i + LIST_PAGE_DETAIL_CHUNK_SIZE));
      }

      const chunkResults = await Promise.all(chunks.map(async (ids) => {
        const { data, error } = await supabase
          .from('tickets')
          .select(`
            *,
            customer:customers!tickets_customer_id_fkey (
              id,
              company,
              vorname,
              nachname,
              zustaendiger_mitarbeiter_id
            ),
            assigned_to:xtax_staff!tickets_assigned_to_staff_id_fkey (
              id,
              full_name,
              initials
            ),
            customer_group:customer_groups!tickets_customer_group_id_fkey (
              id,
              name
            )
          `)
          .in('id', ids)
          .limit(ids.length)
          .returns<RawTicketWithJoins[]>();

        if (error) {
          console.error('[TicketService.listTicketsPage] Detail-Load-Error:', error);
          return null;
        }

        const counts = await this.fetchMessageCounts(ids);
        return { rows: data ?? [], counts };
      }));

      if (chunkResults.some(r => r === null)) {
        return { success: false, tickets: [], total_count: 0 };
      }

      const rawById: Record<string, RawTicketWithJoins> = {};
      const countsById: Record<string, number> = {};
      for (const result of chunkResults) {
        if (!result) continue;
        for (const row of result.rows) {
          rawById[row.id] = row;
        }
        Object.assign(countsById, result.counts);
      }

      const tickets: TicketListView[] = [];
      for (const id of orderedIds) {
        const raw = rawById[id];
        if (!raw) continue;
        tickets.push(this.mapRawToListView(raw, countsById[id] ?? 0));
      }

      return { success: true, tickets, total_count: totalCount };
    } catch (error) {
      console.error('[TicketService.listTicketsPage] Unexpected error:', error);
      return { success: false, tickets: [], total_count: 0 };
    }
  }

  /**
   * Liest die gespeicherte Standardansicht der Ticketliste eines Mitarbeiters
   * (xtax_staff.ticket_standard_ansicht). null bei Fehler oder unbekanntem Wert.
   */
  async getStandardView(staffId: string): Promise<TicketView | null> {
    try {
      const { data, error } = await supabase
        .from('xtax_staff')
        .select('ticket_standard_ansicht')
        .eq('id', staffId)
        .maybeSingle();

      if (error) {
        console.error('[TicketService.getStandardView] Load-Error:', error);
        return null;
      }

      const value = (data as { ticket_standard_ansicht?: string | null } | null)?.ticket_standard_ansicht;
      if (value === 'mine' || value === 'pool' || value === 'all') {
        return value;
      }
      return null;
    } catch (error) {
      console.error('[TicketService.getStandardView] Unexpected error:', error);
      return null;
    }
  }

  /**
   * Speichert die Standardansicht der Ticketliste fuer den angemeldeten
   * Mitarbeiter. Gegenueber der Datenbank melden sich alle Mitarbeiter ueber
   * ein gemeinsames technisches Konto an; die Person ermittelt das Intranet aus
   * der Google-Anmeldung. Deshalb wird die Mitarbeiter-ID uebergeben (gleiches
   * Muster wie Zuweisung und Audit-Felder). Die RPC set_ticket_standard_ansicht
   * prueft die Mitarbeiter-Rolle und schreibt nur fuer einen aktiven Mitarbeiter.
   */
  async setStandardView(staffId: string, view: TicketView): Promise<TicketOperationResult> {
    try {
      const { data, error } = await supabase
        .rpc('set_ticket_standard_ansicht', { p_staff_id: staffId, p_ansicht: view });

      if (error) {
        console.error('[TicketService.setStandardView] RPC-Error:', error);
        return { success: false, error: error.message };
      }

      if (data !== view) {
        console.error('[TicketService.setStandardView] Unerwartete Antwort:', data);
        return { success: false, error: 'Die Standardansicht wurde nicht bestätigt.' };
      }

      return { success: true };
    } catch (error) {
      console.error('[TicketService.setStandardView] Unexpected error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }

  /**
   * Laedt ein Ticket inkl. aller Messages, Events, Anhaenge, Customer, Staff.
   */
  async getTicketById(id: string): Promise<TicketDetailView | null> {
    try {
      // 1. Ticket-Kopf inkl. Customer (voll) + Assignee + Creator
      const { data: ticketData, error: ticketError } = await supabase
        .from('tickets')
        .select(`
          *,
          customer:customers!tickets_customer_id_fkey (*),
          customer_group:customer_groups!tickets_customer_group_id_fkey (id, name),
          assigned_to:xtax_staff!tickets_assigned_to_staff_id_fkey (id, full_name, initials),
          created_by:xtax_staff!tickets_created_by_staff_id_fkey (id, full_name, initials)
        `)
        .eq('id', id)
        .maybeSingle();

      if (ticketError) {
        console.error('[TicketService.getTicketById] Ticket-Load-Error:', ticketError);
        return null;
      }
      if (!ticketData) return null;

      // Cast nach unknown wegen PostgREST-Embed-Type-Inferenz (siehe KONVENTION oben)
      const ticket = ticketData as unknown as RawTicketDetail;

      // 2. Messages inkl. Author-Staff
      const { data: messagesData, error: messagesError } = await supabase
        .from('ticket_messages')
        .select(`
          *,
          author_staff:xtax_staff!ticket_messages_author_staff_id_fkey (id, full_name, initials)
        `)
        .eq('ticket_id', id)
        // Ausgeblendete Nachrichten (ausgeblendet_grund gesetzt, z. B. importierte
        // Zendesk-OCR-Notizen, deren Text inzwischen am Anhang steht) bleiben in
        // der Datenbank erhalten, erscheinen aber nicht im Verlauf.
        .is('ausgeblendet_grund', null)
        .order('created_at', { ascending: true })
        .limit(1000)
        .returns<RawTicketMessageWithStaff[]>();

      if (messagesError) {
        console.error('[TicketService.getTicketById] Messages-Load-Error:', messagesError);
        return null;
      }

      const messages = messagesData ?? [];
      const messageIds = messages.map(m => m.id);

      // 3. Attachments fuer alle Messages — regulaere und Inline-Bilder trennen.
      //    Regulaere landen in der Anhangsliste; Inline-Bilder (is_inline) dienen
      //    nur dem Aufloesen von cid:-Referenzen im HTML und tauchen NICHT in der
      //    Anhangsliste auf.
      const attachmentsByMessage: Record<string, TicketAttachment[]> = {};
      const inlineByMessage: Record<string, TicketAttachment[]> = {};
      const inlineStoragePaths: string[] = [];
      if (messageIds.length > 0) {
        const { data: attachmentsData, error: attachmentsError } = await supabase
          .from('ticket_attachments')
          .select('*')
          .in('message_id', messageIds)
          .limit(1000);

        if (attachmentsError) {
          console.error('[TicketService.getTicketById] Attachments-Load-Error:', attachmentsError);
        } else {
          const attachments = (attachmentsData ?? []) as TicketAttachment[];
          for (const att of attachments) {
            if (att.is_inline) {
              if (!inlineByMessage[att.message_id]) inlineByMessage[att.message_id] = [];
              inlineByMessage[att.message_id].push(att);
              inlineStoragePaths.push(att.storage_path);
            } else {
              if (!attachmentsByMessage[att.message_id]) attachmentsByMessage[att.message_id] = [];
              attachmentsByMessage[att.message_id].push(att);
            }
          }
        }
      }

      // Signierte URLs fuer alle Inline-Bilder vorab erzeugen (Bucket ist privat).
      // Gueltigkeit 1h, damit eingebettete Bilder beim Lesen nicht verfallen.
      const signedUrlByPath: Record<string, string> = {};
      if (inlineStoragePaths.length > 0) {
        const { data: signedData, error: signedError } = await supabase
          .storage
          .from('ticket-attachments')
          .createSignedUrls(inlineStoragePaths, 3600);
        if (signedError) {
          console.error('[TicketService.getTicketById] Inline-SignedUrls-Error:', signedError.message);
        } else if (signedData) {
          for (const entry of signedData) {
            if (entry.path && entry.signedUrl) {
              signedUrlByPath[entry.path] = entry.signedUrl;
            }
          }
        }
      }

      // 4. Events
      const { data: eventsData, error: eventsError } = await supabase
        .from('ticket_events')
        .select('*')
        .eq('ticket_id', id)
        .order('created_at', { ascending: true })
        .limit(1000);

      if (eventsError) {
        console.error('[TicketService.getTicketById] Events-Load-Error:', eventsError);
      }

      // 5. Outbound-Empfaenger: outbound_mail_log per ticket_id laden und ueber
      //    message_id_header (case-insensitive) den ausgehenden Messages zuordnen.
      //    Separate Query + Client-Merge — analog zu Messages/Attachments/Events.
      const recipientsByMid: Record<string, string[]> = {};
      const ccByMid: Record<string, string[]> = {};
      {
        const { data: outboundData, error: outboundError } = await supabase
          .from('outbound_mail_log')
          .select('message_id_header, to_emails, cc_emails')
          .eq('ticket_id', id)
          .limit(1000);

        if (outboundError) {
          console.error('[TicketService.getTicketById] Outbound-Load-Error:', outboundError);
        } else {
          const rows = (outboundData ?? []) as Array<{ message_id_header: string | null; to_emails: string[] | null; cc_emails: string[] | null }>;
          for (const row of rows) {
            if (!row.message_id_header) continue;
            const mid = row.message_id_header.toLowerCase();
            if (Array.isArray(row.to_emails) && row.to_emails.length > 0) {
              recipientsByMid[mid] = row.to_emails;
            }
            if (Array.isArray(row.cc_emails) && row.cc_emails.length > 0) {
              ccByMid[mid] = row.cc_emails;
            }
          }
        }
      }

      // 5b. Eingehende Empfaenger: ticket_inbound_log per ticket_id laden und ueber
      //     message_id (= ticket_messages.id) den eingehenden Messages zuordnen.
      //     To/CC der eingehenden Mail liegen hier, nicht auf ticket_messages.
      const inboundToByMsgId: Record<string, string[]> = {};
      const inboundCcByMsgId: Record<string, string[]> = {};
      {
        const { data: inboundData, error: inboundError } = await supabase
          .from('ticket_inbound_log')
          .select('message_id, to_emails, cc_emails')
          .eq('ticket_id', id)
          .not('message_id', 'is', null)
          .limit(1000);

        if (inboundError) {
          console.error('[TicketService.getTicketById] Inbound-Load-Error:', inboundError);
        } else {
          const rows = (inboundData ?? []) as Array<{ message_id: string | null; to_emails: string[] | null; cc_emails: string[] | null }>;
          for (const row of rows) {
            if (!row.message_id) continue;
            if (Array.isArray(row.to_emails) && row.to_emails.length > 0) {
              inboundToByMsgId[row.message_id] = row.to_emails;
            }
            if (Array.isArray(row.cc_emails) && row.cc_emails.length > 0) {
              inboundCcByMsgId[row.message_id] = row.cc_emails;
            }
          }
        }
      }

      // 6. Geplante Sendungen (pending/sending/failed) fuer die "Geplant"-Anzeige.
      //    Separate Query + Merge, analog zu den uebrigen Relationen (keine Embeds).
      const { data: scheduledData, error: scheduledError } = await supabase
        .from('ticket_scheduled_messages')
        .select('id, ticket_id, body_text, body_html, to_emails, cc_emails, scheduled_at, status, created_by_staff_id, created_by_email, created_by_name, sent_as_staff_id, sent_as_name, created_at, sent_at, sent_message_id, attempts, last_error')
        .eq('ticket_id', id)
        .in('status', ['pending', 'sending', 'failed'])
        .order('scheduled_at', { ascending: true })
        .limit(1000);

      if (scheduledError) {
        console.error('[TicketService.getTicketById] Scheduled-Load-Error:', scheduledError);
      }

      const messagesWithAttachments: TicketMessageWithAttachments[] = messages.map(m => ({
        id: m.id,
        ticket_id: m.ticket_id,
        author_type: m.author_type,
        author_staff_id: m.author_staff_id,
        author_email: m.author_email,
        author_name: m.author_name,
        sent_as_staff_id: m.sent_as_staff_id,
        sent_as_name: m.sent_as_name,
        body_text: m.body_text,
        body_html: rewriteCidReferences(m.body_html, inlineByMessage[m.id], signedUrlByPath),
        is_internal_note: m.is_internal_note,
        message_type: m.message_type,
        gmail_message_id: m.gmail_message_id,
        message_id_header: m.message_id_header,
        in_reply_to_header: m.in_reply_to_header,
        references_header: m.references_header,
        direction: m.direction,
        created_at: m.created_at,
        author_staff: m.author_staff,
        attachments: attachmentsByMessage[m.id] ?? [],
        to_emails: (m.message_id_header ? recipientsByMid[m.message_id_header.toLowerCase()] : undefined) ?? inboundToByMsgId[m.id] ?? null,
        cc_emails: (m.message_id_header ? ccByMid[m.message_id_header.toLowerCase()] : undefined) ?? inboundCcByMsgId[m.id] ?? null
      }));

      return {
        id: ticket.id,
        ticket_number: ticket.ticket_number,
        customer_id: ticket.customer_id,
        subject: ticket.subject,
        status: ticket.status,
        priority: ticket.priority,
        assigned_to_staff_id: ticket.assigned_to_staff_id,
        source: ticket.source,
        gmail_thread_id: ticket.gmail_thread_id,
        created_by_staff_id: ticket.created_by_staff_id,
        last_message_at: ticket.last_message_at,
        last_message_from: ticket.last_message_from,
        created_at: ticket.created_at,
        updated_at: ticket.updated_at,
        closed_at: ticket.closed_at,
        customer_group_id: ticket.customer_group_id ?? null,
        customer: ticket.customer,
        customer_group: ticket.customer_group ?? null,
        assigned_to: ticket.assigned_to,
        created_by: ticket.created_by,
        messages: messagesWithAttachments,
        events: (eventsData ?? []) as TicketEvent[],
        scheduled_messages: (scheduledData ?? []) as ScheduledTicketMessage[]
      };
    } catch (error) {
      console.error('[TicketService.getTicketById] Unexpected error:', error);
      return null;
    }
  }

  /**
   * Open-Ticket-Stats fuer das Navigations-Badge.
   * "open" = status in (new, open, pending).
   *
   * Gezaehlt wird ueber die RPC get_ticket_open_stats mit derselben
   * Ansichts-Definition wie die Ticketliste (DB-Funktion ticket_in_ansicht);
   * als Duplikat ausgeblendete Tickets (list_hidden) zaehlen nicht mit.
   * Ohne currentStaffId ist mine_open 0.
   */
  async getOpenTicketStats(currentStaffId: string | null): Promise<TicketStats> {
    try {
      const { data, error } = await supabase
        .rpc('get_ticket_open_stats', { p_staff_id: currentStaffId });

      if (error) {
        console.error('[TicketService.getOpenTicketStats] Error:', error);
        return { total_open: 0, mine_open: 0, pool_open: 0 };
      }

      const row = (Array.isArray(data) ? data[0] : data) as RawTicketOpenStatsRow | null | undefined;
      if (!row) {
        return { total_open: 0, mine_open: 0, pool_open: 0 };
      }

      return {
        total_open: Number(row.total_open) || 0,
        mine_open: Number(row.mine_open) || 0,
        pool_open: Number(row.pool_open) || 0
      };
    } catch (error) {
      console.error('[TicketService.getOpenTicketStats] Unexpected error:', error);
      return { total_open: 0, mine_open: 0, pool_open: 0 };
    }
  }

  /**
   * Stellt den Kontext für die KI-Prüfung (Voll-Modus) zusammen: den
   * vollständigen kundenseitig sichtbaren Verlauf (ohne interne Notizen, ohne
   * System-Nachrichten), den korrekt geschriebenen Namen des Anfragenden
   * (author_name der jüngsten eingehenden Kundennachricht) und eine fertig
   * formulierte Anrede (salutation) aus der Empfänger-Auflösung. Eine Query auf
   * ticket_messages; body_text wird bevorzugt, sonst HTML-Strip-Fallback.
   *
   * writerStaffEmail (optional): E-Mail des antwortenden Mitarbeiters für das
   * Duz-Gate — eine aufgelöste "Du"-Anrede bleibt nur bestehen, wenn dieser
   * Mitarbeiter in der Duz-Liste (customers.duz_mitarbeiter_ids) des
   * Ticket-Kunden steht; in jedem Zweifels- oder Fehlerfall konservativ "Sie".
   */
  async getProofreadContext(
    ticketId: string,
    writerStaffEmail?: string | null
  ): Promise<{ requester_name: string | null; thread: ProofreadThreadMessage[]; salutation: string | null }> {
    try {
      const { data, error } = await supabase
        .from('ticket_messages')
        .select('author_type, author_name, author_email, body_text, body_html, created_at')
        .eq('ticket_id', ticketId)
        .eq('is_internal_note', false)
        .order('created_at', { ascending: true })
        .limit(1000);

      if (error) {
        console.error('[TicketService.getProofreadContext] Load-Error:', error);
        return { requester_name: null, thread: [], salutation: null };
      }

      const rows = (data ?? []) as Array<{
        author_type: string | null;
        author_name: string | null;
        author_email: string | null;
        body_text: string | null;
        body_html: string | null;
        created_at: string;
      }>;

      const thread: ProofreadThreadMessage[] = [];
      let requesterName: string | null = null;
      let writerEmail: string | null = null;

      for (const row of rows) {
        const from = row.author_type;
        if (from !== 'customer' && from !== 'staff') continue;

        const text = (row.body_text && row.body_text.trim())
          ? row.body_text.trim()
          : stripHtmlToPlainText(row.body_html || '');
        if (!text) continue;

        thread.push({ from: from as 'customer' | 'staff', text });

        if (from === 'customer' && row.author_name && row.author_name.trim()) {
          requesterName = row.author_name.trim();
        }
        if (from === 'customer' && row.author_email && row.author_email.trim()) {
          writerEmail = row.author_email.trim();
        }
      }

      // Empfänger der Anrede auflösen: vorrangig die TATSÄCHLICH schreibende
      // Person (über ihre E-Mail), damit Geschlecht und Nachname zusammenpassen;
      // sonst der Hauptkontakt des Kunden (z. B. bei vom Team initiierten
      // Tickets). Greift nichts, bleibt es bei der generischen Begrüßung ohne
      // Namen. Teil-Fehler degradieren still (best-effort).
      // Anschließend Duz-Gate: eine "Du"-Anrede bleibt nur bestehen, wenn der
      // antwortende Mitarbeiter (writerStaffEmail) in der Duz-Liste des
      // Ticket-Kunden steht — sonst konservativ "Sie".
      const recipient = await this.resolveSalutationPerson(ticketId, writerEmail);
      const gated = await this.enforceDuzPermission(recipient, {
        ticketId,
        writerStaffEmail: writerStaffEmail ?? null
      });
      const salutation = buildSalutation(gated);

      return { requester_name: requesterName, thread, salutation };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[TicketService.getProofreadContext] Unexpected error:', msg);
      return { requester_name: null, thread: [], salutation: null };
    }
  }

  /**
   * Bildet die Begrüßung für ein NEUES Ticket aus dem gewählten Kunden (dessen
   * Hauptkontakt). Es gibt hier noch keinen Verlauf und keinen Schreiber, daher
   * wird der Kunden-Datensatz angesprochen. Gleiche Anrede-Logik wie bei
   * Antworten — eine einzige Quelle der Wahrheit. Ohne Kunde eine generische
   * Begrüßung ("Guten Tag,").
   *
   * Die Anrede durchläuft das Duz-Gate (enforceDuzPermission): "Du" bleibt nur
   * bestehen, wenn der Absender (writerStaffId) in customers.duz_mitarbeiter_ids
   * steht. Die Duz-Liste wird aus dem übergebenen Objekt verwendet, sofern
   * vorhanden; sonst wird sie per customer.id nachgeladen. In jedem Zweifels-
   * oder Fehlerfall konservativ "Sie".
   */
  async buildSalutationForCustomer(
    customer: {
      id?: string | null;
      anrede?: string | null;
      geschlecht?: string | null;
      vorname?: string | null;
      nachname?: string | null;
      duz_mitarbeiter_ids?: string[] | null;
    } | null,
    writerStaffId?: string | null
  ): Promise<string> {
    if (!customer) {
      return buildSalutation(null);
    }
    const person: SalutationPerson = {
      anrede: customer.anrede ?? null,
      geschlecht: customer.geschlecht ?? null,
      vorname: customer.vorname ?? null,
      nachname: customer.nachname ?? null
    };
    const gated = await this.enforceDuzPermission(person, {
      writerStaffId: writerStaffId ?? null,
      duzMitarbeiterIds: Array.isArray(customer.duz_mitarbeiter_ids) ? customer.duz_mitarbeiter_ids : undefined,
      customerId: customer.id ?? null
    });
    return buildSalutation(gated);
  }

  /**
   * Löst die anzusprechende Person für die Begrüßung auf. Reihenfolge:
   *   R1: Schreiber-E-Mail trifft einen aktiven Kontakt des Ticket-Kunden
   *       (case-insensitiv in JS, um ILIKE-Sonderzeichen im local-part zu
   *       vermeiden) → Geschlecht UND Nachname derselben Person.
   *   R2: Schreiber-E-Mail = customers.email des Tickets → Kunden-Hauptkontakt.
   *   R4: kein Schreiber bekannt (z. B. vom Team initiiertes Ticket) → Kunden-
   *       Hauptkontakt.
   *   R3/R5: Schreiber bekannt, aber kein Treffer, bzw. gar kein Kunde → null
   *       (buildSalutation grüßt dann generisch mit "Guten Tag," ohne Namen).
   * Best-effort: Teil-Fehler werden geloggt und als "kein Treffer" behandelt.
   */
  private async resolveSalutationPerson(
    ticketId: string,
    writerEmail: string | null
  ): Promise<SalutationPerson | null> {
    // Kunden des Tickets ermitteln. Ohne Kundenzuordnung (Pool- oder Gruppenticket) können wir
    // den Schreiber keinem Kontakt sicher zuordnen → generische Begrüßung ohne
    // Namen (übernimmt buildSalutation).
    const { data: ticketRow, error: ticketError } = await supabase
      .from('tickets')
      .select('customer_id')
      .eq('id', ticketId)
      .maybeSingle();

    if (ticketError) {
      console.error('[TicketService.resolveSalutationPerson] Ticket-Load-Error:', ticketError);
      return null;
    }

    const customerId = ticketRow?.customer_id ?? null;
    if (!customerId) return null;

    const writerLower = writerEmail ? writerEmail.toLowerCase() : null;

    // R1: tatsächlicher Schreiber als aktiver Kontakt des Kunden (E-Mail-Match,
    // case-insensitiv in JS — vermeidet ILIKE-Sonderzeichen im local-part).
    // Geschlecht und Nachname stammen dann von derselben Person.
    if (writerLower) {
      const { data: contacts, error: contactsError } = await supabase
        .from('customer_contacts')
        .select('anrede, geschlecht, vorname, nachname, email')
        .eq('customer_id', customerId)
        .eq('is_active', true)
        .limit(1000);

      if (contactsError) {
        console.error('[TicketService.resolveSalutationPerson] Kontakt-Load-Error:', contactsError);
      } else if (Array.isArray(contacts)) {
        const match = contacts.find(
          c => typeof c.email === 'string' && c.email.trim().toLowerCase() === writerLower
        );
        if (match) return match as SalutationPerson;
      }
    }

    // Für R2/R4 den Kunden-Datensatz (Hauptkontakt) laden.
    const { data: customer, error: customerError } = await supabase
      .from('customers')
      .select('anrede, geschlecht, vorname, nachname, email')
      .eq('id', customerId)
      .maybeSingle();

    if (customerError) {
      console.error('[TicketService.resolveSalutationPerson] Kunden-Load-Error:', customerError);
      return null;
    }
    if (!customer) return null;

    const customerEmailLower =
      typeof customer.email === 'string' ? customer.email.trim().toLowerCase() : '';

    // R2: der Schreiber IST der Hauptkontakt (Kunden-E-Mail trifft).
    if (writerLower && customerEmailLower && customerEmailLower === writerLower) {
      return customer as SalutationPerson;
    }

    // R4: kein Schreiber bekannt → Hauptkontakt des Kunden ansprechen.
    if (!writerLower) {
      return customer as SalutationPerson;
    }

    // R3: Schreiber bekannt, aber weder Kontakt noch Kunden-E-Mail → kein
    // Geschlecht raten; buildSalutation grüßt dann ohne Namen.
    return null;
  }

  /**
   * Duz-Gate: Degradiert eine aufgelöste "Du"-Anrede konservativ auf "Sie",
   * wenn der schreibende XTAX-Mitarbeiter nicht in der Duz-Liste
   * (customers.duz_mitarbeiter_ids) des Kunden steht. Nicht-"Du"-Anreden
   * passieren unverändert (das Gate greift nur bei anrede = "Du",
   * case-insensitiv wie in buildSalutation).
   *
   * Absender-Auflösung: bevorzugt writerStaffId; sonst writerStaffEmail gegen
   * die aktiven xtax_staff (Match case-insensitiv in JS — vermeidet
   * ILIKE-Sonderzeichen im local-part, analog resolveSalutationPerson).
   * Duz-Liste: bevorzugt die übergebene duzMitarbeiterIds; sonst Nachladen
   * über customerId bzw. ticketId → tickets.customer_id → customers.
   *
   * Konservativ in JEDEM Zweifels- oder Fehlerfall (kein Absender auflösbar,
   * kein Kunde, leere Liste, Query-Fehler, Exception): "Sie".
   */
  private async enforceDuzPermission(
    person: SalutationPerson | null,
    opts: {
      writerStaffId?: string | null;
      writerStaffEmail?: string | null;
      duzMitarbeiterIds?: string[] | null;
      customerId?: string | null;
      ticketId?: string | null;
    }
  ): Promise<SalutationPerson | null> {
    if (!person) return person;

    const istDu = (person.anrede || '').trim().toLowerCase() === 'du';
    if (!istDu) return person;

    const downgraded: SalutationPerson = { ...person, anrede: 'Sie' };

    try {
      // 1. Absender-Staff-ID bestimmen: direkt uebergeben oder per E-Mail
      //    ueber die aktiven Mitarbeiter aufloesen.
      let staffId = opts.writerStaffId ?? null;
      if (!staffId) {
        const emailLower = (opts.writerStaffEmail || '').trim().toLowerCase();
        if (!emailLower) return downgraded;

        const { data: staffRows, error: staffError } = await supabase
          .from('xtax_staff')
          .select('id, email')
          .eq('is_active', true)
          .limit(1000);

        if (staffError || !Array.isArray(staffRows)) {
          if (staffError) {
            console.error('[TicketService.enforceDuzPermission] Staff-Load-Error:', staffError);
          }
          return downgraded;
        }

        const match = staffRows.find(
          s => typeof s.email === 'string' && s.email.trim().toLowerCase() === emailLower
        );
        if (!match) return downgraded;
        staffId = match.id as string;
      }

      // 2. Duz-Liste bestimmen: uebergebene Liste verwenden, sonst per
      //    customerId (direkt oder via ticketId aufgeloest) nachladen.
      let duzIds: string[] | null = Array.isArray(opts.duzMitarbeiterIds)
        ? opts.duzMitarbeiterIds
        : null;

      if (duzIds === null) {
        let customerId = opts.customerId ?? null;

        if (!customerId && opts.ticketId) {
          const { data: ticketRow, error: ticketError } = await supabase
            .from('tickets')
            .select('customer_id')
            .eq('id', opts.ticketId)
            .maybeSingle();

          if (ticketError) {
            console.error('[TicketService.enforceDuzPermission] Ticket-Load-Error:', ticketError);
            return downgraded;
          }
          customerId = ticketRow?.customer_id ?? null;
        }

        if (!customerId) return downgraded;

        const { data: customerRow, error: customerError } = await supabase
          .from('customers')
          .select('duz_mitarbeiter_ids')
          .eq('id', customerId)
          .maybeSingle();

        if (customerError || !customerRow) {
          if (customerError) {
            console.error('[TicketService.enforceDuzPermission] Kunden-Load-Error:', customerError);
          }
          return downgraded;
        }
        duzIds = Array.isArray(customerRow.duz_mitarbeiter_ids)
          ? (customerRow.duz_mitarbeiter_ids as string[])
          : [];
      }

      // 3. Erlaubnis-Check: nur bei Treffer bleibt die Du-Anrede bestehen.
      return duzIds.includes(staffId) ? person : downgraded;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[TicketService.enforceDuzPermission] Unexpected error:', msg);
      return downgraded;
    }
  }

  // ===========================================================================
  // CREATE TICKET
  // ===========================================================================

  /**
   * Legt ein neues Ticket inkl. erster Message an.
   * - send_email=true: Erste Message wird per Mail rausgeschickt; Backend
   *   persistiert die Anhaenge nach dem Versand.
   * - send_email=false: Reines internes Ticket; Anhaenge werden hier ueber
   *   attachToMessage in den Storage geladen.
   */
  async createTicket(
    input: CreateTicketInput,
    actor: TicketActor
  ): Promise<CreateTicketResult> {
    try {
      // 1. Ticket-Datensatz anlegen (ticket_number wird per DB-Default vergeben)
      const { data: ticketData, error: ticketError } = await supabase
        .from('tickets')
        .insert({
          customer_id: input.customer_id,
          subject: input.subject,
          status: 'open',
          priority: input.priority ?? 'normal',
          assigned_to_staff_id: input.assigned_to_staff_id ?? actor.staff_id,
          source: 'intranet',
          created_by_staff_id: actor.staff_id,
          last_message_at: new Date().toISOString(),
          last_message_from: 'staff'
        })
        .select('*')
        .single();

      if (ticketError || !ticketData) {
        console.error('[TicketService.createTicket] Ticket-Insert-Error:', ticketError);
        return { success: false, error: ticketError?.message ?? 'Ticket-Anlage fehlgeschlagen' };
      }

      const ticket = ticketData as Ticket;

      // 2. Erste Message anlegen. Aussenidentitaet (sent_as_*) nur gesetzt, wenn
      //    "Senden als andere Person" gewaehlt wurde; author_* bleibt die echte Person.
      const direction = input.send_email ? 'outbound' : 'internal';
      const { data: messageData, error: messageError } = await supabase
        .from('ticket_messages')
        .insert({
          ticket_id: ticket.id,
          author_type: 'staff',
          author_staff_id: actor.staff_id,
          author_email: actor.staff_email,
          author_name: actor.staff_full_name ?? null,
          sent_as_staff_id: input.sent_as?.staff_id ?? null,
          sent_as_name: input.sent_as?.name ?? null,
          body_text: input.body_text,
          body_html: input.body_html ?? null,
          is_internal_note: false,
          direction
        })
        .select('*')
        .single();

      if (messageError || !messageData) {
        console.error('[TicketService.createTicket] Message-Insert-Error:', messageError);
        // Ticket bleibt bestehen, aber wir markieren als Teilfehler
        return {
          success: false,
          ticket,
          error: 'Ticket angelegt, aber erste Nachricht konnte nicht gespeichert werden'
        };
      }

      const firstMessage = messageData as TicketMessage;
      const hasAttachments = Array.isArray(input.attachments) && input.attachments.length > 0;
      const hasInline = Array.isArray(input.inline_images) && input.inline_images.length > 0;

      // 3. Audit-Event 'created'
      await this.logEvent({
        ticket_id: ticket.id,
        event_type: 'created',
        from_value: null,
        to_value: ticket.ticket_number,
        actor,
        metadata: {
          subject: ticket.subject,
          customer_id: ticket.customer_id,
          assigned_to: ticket.assigned_to_staff_id,
          send_email: input.send_email === true,
          ...(input.sent_as ? { sent_as_staff_id: input.sent_as.staff_id, sent_as_name: input.sent_as.name } : {})
        }
      });

      // 4. Anhang-Persistenz:
      //    - send_email=true: Backend (send-ticket-reply.js) macht Upload nach Mailversand.
      //    - send_email=false: Frontend macht Upload jetzt direkt.
      if (!input.send_email && (hasAttachments || hasInline)) {
        await this.attachToMessage(ticket.id, firstMessage.id, [
          ...(input.attachments ?? []),
          ...(input.inline_images ?? [])
        ]);
      }

      // 5. Optional Mail-Versand
      let mailSent = false;
      let mailError: string | undefined;

      if (input.send_email) {
        if (!input.to_emails || input.to_emails.length === 0) {
          mailError = 'send_email=true, aber keine Empfänger-Adresse angegeben';
          console.warn('[TicketService.createTicket]', mailError);
        } else {
          const sendResult = await ticketMailService.sendReply({
            ticket_id: ticket.id,
            ticket_number: ticket.ticket_number,
            subject: ticket.subject,
            body_text: input.body_text,
            body_html: input.body_html,
            to_emails: input.to_emails,
            cc_emails: input.cc_emails,
            attachments: input.attachments,
            inline_images: input.inline_images,
            actor,
            sent_as: input.sent_as,
            is_first_message: true,
            message_id: firstMessage.id
          });
          mailSent = sendResult.success;
          mailError = sendResult.error;
        }
      }

      return {
        success: true,
        ticket,
        first_message: firstMessage,
        mail_sent: mailSent,
        mail_error: mailError
      };
    } catch (error) {
      console.error('[TicketService.createTicket] Unexpected error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }

  // ===========================================================================
  // ADD MESSAGE / INTERNAL NOTE
  // ===========================================================================

  /**
   * Fuegt eine Nachricht (oder interne Notiz) an ein bestehendes Ticket an.
   * - is_internal_note=true: Notiz, Frontend laedt Anhaenge selbst.
   * - is_internal_note=false + send_email=true: Mail-Reply, Backend laedt Anhaenge.
   * - is_internal_note=false + send_email=false: Outbound ohne Mail, Frontend laedt
   *   Anhaenge analog Notiz.
   */
  async addMessage(
    input: AddMessageInput,
    actor: TicketActor
  ): Promise<AddMessageResult> {
    try {
      // Ticket laden (fuer subject, ticket_number, customer)
      const { data: ticketData, error: ticketError } = await supabase
        .from('tickets')
        .select('id, ticket_number, subject, status')
        .eq('id', input.ticket_id)
        .maybeSingle();

      if (ticketError || !ticketData) {
        return { success: false, error: 'Ticket nicht gefunden' };
      }

      const direction = input.is_internal_note
        ? 'internal'
        : (input.send_email ? 'outbound' : 'internal');

      // Aussenidentitaet (sent_as_*) nur gesetzt, wenn "Senden als andere Person"
      // gewaehlt wurde; author_* bleibt die echte Person (Audit).
      const { data: messageData, error: messageError } = await supabase
        .from('ticket_messages')
        .insert({
          ticket_id: input.ticket_id,
          author_type: 'staff',
          author_staff_id: actor.staff_id,
          author_email: actor.staff_email,
          author_name: actor.staff_full_name ?? null,
          sent_as_staff_id: input.sent_as?.staff_id ?? null,
          sent_as_name: input.sent_as?.name ?? null,
          body_text: input.body_text,
          body_html: input.body_html ?? null,
          is_internal_note: input.is_internal_note,
          direction
        })
        .select('*')
        .single();

      if (messageError || !messageData) {
        console.error('[TicketService.addMessage] Insert-Error:', messageError);
        return { success: false, error: messageError?.message ?? 'Nachricht konnte nicht gespeichert werden' };
      }

      const message = messageData as TicketMessage;
      const hasAttachments = Array.isArray(input.attachments) && input.attachments.length > 0;
      const hasInline = Array.isArray(input.inline_images) && input.inline_images.length > 0;
      const useMailPath = !input.is_internal_note && input.send_email === true;

      // last_message_at / last_message_from im Ticket updaten (nur bei sichtbarer Nachricht)
      if (!input.is_internal_note) {
        await supabase
          .from('tickets')
          .update({
            last_message_at: message.created_at,
            last_message_from: 'staff',
            // Status-Auto-Transition: 'new' → 'open' beim ersten Staff-Reply
            status: ticketData.status === 'new' ? 'open' : ticketData.status
          })
          .eq('id', input.ticket_id);
      }

      // Anhang-Persistenz: Frontend uebernimmt, wenn der Mail-Pfad NICHT genommen wird
      // (interne Notiz, oder Outbound ohne Mailversand).
      if (!useMailPath && (hasAttachments || hasInline)) {
        await this.attachToMessage(input.ticket_id, message.id, [
          ...(input.attachments ?? []),
          ...(input.inline_images ?? [])
        ]);
      }

      // Audit-Event
      await this.logEvent({
        ticket_id: input.ticket_id,
        event_type: input.is_internal_note ? 'internal_note_added' : 'message_sent',
        from_value: null,
        to_value: null,
        actor,
        metadata: {
          message_id: message.id,
          send_email: input.send_email === true && !input.is_internal_note,
          attachment_count: hasAttachments && input.attachments ? input.attachments.length : 0,
          ...(input.sent_as ? { sent_as_staff_id: input.sent_as.staff_id, sent_as_name: input.sent_as.name } : {})
        }
      });

      // Mail-Versand (nur fuer reguläre Antwort mit send_email=true)
      let mailSent = false;
      let mailError: string | undefined;

      if (useMailPath) {
        if (!input.to_emails || input.to_emails.length === 0) {
          mailError = 'send_email=true, aber keine Empfänger-Adresse angegeben';
        } else {
          const sendResult = await ticketMailService.sendReply({
            ticket_id: input.ticket_id,
            ticket_number: ticketData.ticket_number,
            subject: ticketData.subject,
            body_text: input.body_text,
            body_html: input.body_html,
            to_emails: input.to_emails,
            cc_emails: input.cc_emails,
            attachments: input.attachments,
            inline_images: input.inline_images,
            actor,
            sent_as: input.sent_as,
            is_first_message: false,
            message_id: message.id
          });
          mailSent = sendResult.success;
          mailError = sendResult.error;
        }
      }

      return {
        success: true,
        message,
        mail_sent: mailSent,
        mail_error: mailError
      };
    } catch (error) {
      console.error('[TicketService.addMessage] Unexpected error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }

  // ===========================================================================
  // FRONTEND ATTACHMENT UPLOAD
  // ===========================================================================

  /**
   * Laedt Anhaenge in den Storage-Bucket und persistiert sie in ticket_attachments.
   * Wird vom Frontend-Pfad genutzt (interne Notiz oder Ticket-Anlage ohne Mailversand);
   * der Mail-Pfad uebernimmt die Persistenz im Backend selbst.
   *
   * Best-effort: einzelne Fehler werden geloggt, aber nicht geworfen — die Message
   * selbst ist zu diesem Zeitpunkt bereits in der DB.
   */
  async attachToMessage(
    ticketId: string,
    messageId: string,
    attachments: TicketAttachmentInput[] | undefined
  ): Promise<void> {
    if (!attachments || attachments.length === 0) return;

    for (let i = 0; i < attachments.length; i++) {
      const att = attachments[i];
      if (!att || !att.filename || !att.content_base64) {
        console.warn('[TicketService.attachToMessage] Anhang uebersprungen (filename oder content fehlt)');
        continue;
      }

      const isInline = att.is_inline === true;
      const storagePath = isInline
        ? `${ticketId}/${messageId}/inline/${i}-${att.filename}`
        : `${ticketId}/${messageId}/${att.filename}`;

      try {
        // base64 → Uint8Array → Blob (Browser-safe, kein Buffer-Polyfill noetig)
        const byteCharacters = atob(att.content_base64);
        const byteArray = new Uint8Array(byteCharacters.length);
        for (let j = 0; j < byteCharacters.length; j++) {
          byteArray[j] = byteCharacters.charCodeAt(j);
        }
        const blob = new Blob([byteArray], {
          type: att.content_type || 'application/octet-stream'
        });

        const { error: uploadError } = await supabase.storage
          .from('ticket-attachments')
          .upload(storagePath, blob, {
            contentType: att.content_type || 'application/octet-stream',
            upsert: true
          });

        if (uploadError) {
          console.warn(`[TicketService.attachToMessage] Storage-Upload fehlgeschlagen fuer ${att.filename}:`, uploadError.message);
          continue;
        }

        const { error: insertError } = await supabase
          .from('ticket_attachments')
          .insert({
            message_id: messageId,
            storage_path: storagePath,
            filename: att.filename,
            content_type: att.content_type || 'application/octet-stream',
            file_size: att.file_size || byteArray.length,
            is_inline: isInline,
            content_id: att.content_id ?? null
          });

        if (insertError) {
          console.warn(`[TicketService.attachToMessage] ticket_attachments-Insert fehlgeschlagen fuer ${att.filename}:`, insertError.message);
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.warn(`[TicketService.attachToMessage] Unerwarteter Fehler bei Anhang ${att.filename}:`, msg);
      }
    }
  }

  /**
   * Loest die Fallback-Empfaengeradresse fuer eine Ticket-Antwort auf, falls am
   * Kunden selbst keine E-Mail hinterlegt ist (customers.email = Stufe 1 wird
   * vom Composer synchron behandelt). Reihenfolge:
   *   Stufe 2: E-Mail des primaeren, aktiven Kontakts aus customer_contacts
   *            (is_primary zuerst, dann sort_order).
   *   Stufe 3: author_email der letzten eingehenden Nachricht des Tickets.
   * Liefert die erste gefundene Adresse oder null.
   */
  async resolveFallbackRecipientEmail(ticketId: string): Promise<string | null> {
    try {
      // Stufe 2: primaerer/aktiver Kontakt des zugeordneten Kunden.
      const { data: ticketRow, error: ticketError } = await supabase
        .from('tickets')
        .select('customer_id')
        .eq('id', ticketId)
        .maybeSingle();

      if (ticketError) {
        console.error('[TicketService.resolveFallbackRecipientEmail] Ticket-Load-Error:', ticketError);
      }

      const customerId = ticketRow?.customer_id ?? null;
      if (customerId) {
        const { data: contact, error: contactError } = await supabase
          .from('customer_contacts')
          .select('email')
          .eq('customer_id', customerId)
          .eq('is_active', true)
          .order('is_primary', { ascending: false })
          .order('sort_order', { ascending: true })
          .limit(1)
          .maybeSingle();

        if (contactError) {
          console.error('[TicketService.resolveFallbackRecipientEmail] Kontakt-Load-Error:', contactError);
        } else if (contact?.email && contact.email.trim()) {
          return contact.email.trim();
        }
      }

      // Stufe 3: author_email der letzten eingehenden Nachricht.
      const { data: lastInbound, error: inboundError } = await supabase
        .from('ticket_messages')
        .select('author_email')
        .eq('ticket_id', ticketId)
        .eq('direction', 'inbound')
        .not('author_email', 'is', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (inboundError) {
        console.error('[TicketService.resolveFallbackRecipientEmail] Inbound-Load-Error:', inboundError);
      } else if (lastInbound?.author_email && lastInbound.author_email.trim()) {
        return lastInbound.author_email.trim();
      }

      return null;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[TicketService.resolveFallbackRecipientEmail] Unerwarteter Fehler:', msg);
      return null;
    }
  }

  // ===========================================================================
  // STATUS / ASSIGNEE / PRIORITY / CUSTOMER UPDATES
  // ===========================================================================

  async updateStatus(
    ticketId: string,
    newStatus: TicketStatus,
    actor: TicketActor
  ): Promise<TicketOperationResult> {
    try {
      const { data: current, error: loadError } = await supabase
        .from('tickets')
        .select('status')
        .eq('id', ticketId)
        .maybeSingle();

      if (loadError || !current) {
        return { success: false, error: 'Ticket nicht gefunden' };
      }

      if (current.status === newStatus) {
        return { success: true };
      }

      const updatePayload: { status: TicketStatus; closed_at: string | null } = {
        status: newStatus,
        closed_at: newStatus === 'closed' ? new Date().toISOString() : null
      };

      const { error: updateError } = await supabase
        .from('tickets')
        .update(updatePayload)
        .eq('id', ticketId);

      if (updateError) {
        console.error('[TicketService.updateStatus] Update-Error:', updateError);
        return { success: false, error: updateError.message };
      }

      // Event-Typ bestimmen: 'closed' hat Vorrang; danach reopen aus
      // resolved/closed; sonst generischer status_changed.
      // Hinweis: der erste Zweig garantiert, dass im zweiten Zweig newStatus
      // nicht 'closed' ist — ein zusaetzlicher Check waere TS-redundant.
      const eventType: TicketEventType =
        newStatus === 'closed' ? 'closed' :
        (current.status === 'closed' || current.status === 'resolved') ? 'reopened' :
        'status_changed';

      await this.logEvent({
        ticket_id: ticketId,
        event_type: eventType,
        from_value: current.status,
        to_value: newStatus,
        actor,
        metadata: {}
      });

      return { success: true };
    } catch (error) {
      console.error('[TicketService.updateStatus] Unexpected error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }

  async assignTicket(
    ticketId: string,
    staffId: string | null,
    actor: TicketActor
  ): Promise<TicketOperationResult> {
    try {
      const { data: current, error: loadError } = await supabase
        .from('tickets')
        .select('assigned_to_staff_id')
        .eq('id', ticketId)
        .maybeSingle();

      if (loadError || !current) {
        return { success: false, error: 'Ticket nicht gefunden' };
      }

      if (current.assigned_to_staff_id === staffId) {
        return { success: true };
      }

      const { error: updateError } = await supabase
        .from('tickets')
        .update({ assigned_to_staff_id: staffId })
        .eq('id', ticketId);

      if (updateError) {
        console.error('[TicketService.assignTicket] Update-Error:', updateError);
        return { success: false, error: updateError.message };
      }

      await this.logEvent({
        ticket_id: ticketId,
        event_type: staffId ? 'assigned' : 'unassigned',
        from_value: current.assigned_to_staff_id,
        to_value: staffId,
        actor,
        metadata: {}
      });

      return { success: true };
    } catch (error) {
      console.error('[TicketService.assignTicket] Unexpected error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }

  async updatePriority(
    ticketId: string,
    newPriority: TicketPriority,
    actor: TicketActor
  ): Promise<TicketOperationResult> {
    try {
      const { data: current, error: loadError } = await supabase
        .from('tickets')
        .select('priority')
        .eq('id', ticketId)
        .maybeSingle();

      if (loadError || !current) {
        return { success: false, error: 'Ticket nicht gefunden' };
      }

      if (current.priority === newPriority) {
        return { success: true };
      }

      const { error: updateError } = await supabase
        .from('tickets')
        .update({ priority: newPriority })
        .eq('id', ticketId);

      if (updateError) {
        console.error('[TicketService.updatePriority] Update-Error:', updateError);
        return { success: false, error: updateError.message };
      }

      await this.logEvent({
        ticket_id: ticketId,
        event_type: 'priority_changed',
        from_value: current.priority,
        to_value: newPriority,
        actor,
        metadata: {}
      });

      return { success: true };
    } catch (error) {
      console.error('[TicketService.updatePriority] Unexpected error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }

  async changeCustomer(
    ticketId: string,
    newCustomerId: string | null,
    actor: TicketActor
  ): Promise<TicketOperationResult> {
    try {
      const { data: current, error: loadError } = await supabase
        .from('tickets')
        .select('customer_id')
        .eq('id', ticketId)
        .maybeSingle();

      if (loadError || !current) {
        return { success: false, error: 'Ticket nicht gefunden' };
      }

      if (current.customer_id === newCustomerId) {
        return { success: true };
      }

      const { error: updateError } = await supabase
        .from('tickets')
        .update({ customer_id: newCustomerId })
        .eq('id', ticketId);

      if (updateError) {
        console.error('[TicketService.changeCustomer] Update-Error:', updateError);
        return { success: false, error: updateError.message };
      }

      await this.logEvent({
        ticket_id: ticketId,
        event_type: 'customer_changed',
        from_value: current.customer_id,
        to_value: newCustomerId,
        actor,
        metadata: {}
      });

      return { success: true };
    } catch (error) {
      console.error('[TicketService.changeCustomer] Unexpected error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }

  // ===========================================================================
  // KUNDENZUORDNUNG (manuell, inkl. Firmengruppe und Absender-Regeln)
  // ===========================================================================

  /**
   * Laedt den Kontext fuer die manuelle Zuordnung eines Tickets ueber die RPC
   * get_ticket_kunden_kontext: Absender (erste eingehende Kundennachricht),
   * ob eine Adress- bzw. Domainregel waehlbar ist (nicht fuer xtax.de, Domain
   * nicht bei oeffentlichen Anbietern), die Firmengruppe des Tickets und die
   * Vorschlaege. null bei Fehler.
   */
  async getCustomerAssignmentContext(ticketId: string): Promise<TicketKundenKontext | null> {
    try {
      const { data, error } = await supabase
        .rpc('get_ticket_kunden_kontext', { p_ticket_id: ticketId });

      if (error) {
        console.error('[TicketService.getCustomerAssignmentContext] RPC-Error:', error);
        return null;
      }
      if (!data || typeof data !== 'object') return null;

      const raw = data as {
        absender?: string | null;
        domain?: string | null;
        adresse_waehlbar?: boolean;
        domain_waehlbar?: boolean;
        gruppe?: { id: string; name: string; zustaendiger_mitarbeiter_id: string | null } | null;
        vorschlaege?: TicketKundenVorschlag[] | null;
      };

      return {
        absender: raw.absender ?? null,
        domain: raw.domain ?? null,
        adresse_waehlbar: raw.adresse_waehlbar === true,
        domain_waehlbar: raw.domain_waehlbar === true,
        gruppe: raw.gruppe ?? null,
        vorschlaege: Array.isArray(raw.vorschlaege) ? raw.vorschlaege : []
      };
    } catch (error) {
      console.error('[TicketService.getCustomerAssignmentContext] Unexpected error:', error);
      return null;
    }
  }

  /**
   * Alle Firmengruppen (customer_groups, gepflegt in der Umsatzzusammenfuehrung),
   * sortiert nach Name. Fuer die Auswahl "Gruppe, Gesellschaft offen".
   */
  async listCustomerGroups(): Promise<TicketCustomerGroupRef[]> {
    try {
      const { data, error } = await supabase
        .from('customer_groups')
        .select('id, name')
        .order('name', { ascending: true })
        .limit(1000);

      if (error) {
        console.error('[TicketService.listCustomerGroups] Load-Error:', error);
        return [];
      }
      return (data ?? []) as TicketCustomerGroupRef[];
    } catch (error) {
      console.error('[TicketService.listCustomerGroups] Unexpected error:', error);
      return [];
    }
  }

  /**
   * Setzt die Zuordnung eines Tickets: Kunde, Firmengruppe (Gesellschaft offen)
   * oder Pool (beides leer). Ist ein Kunde gesetzt, leert die Datenbank die
   * Gruppe selbst (Trigger tickets_gruppe_bei_kunde_leeren). Protokolliert als
   * customer_changed; die Gruppen stehen in metadata.
   *
   * Optional wird anschliessend eine Absender-Regel angelegt (zuordnung.regel
   * 'adresse' bzw. 'domain', nur zusammen mit einem Kunden). Schlaegt nur die
   * Regel fehl, bleibt die Zuordnung bestehen und der Fehler wird gemeldet.
   */
  async updateCustomerAssignment(
    ticketId: string,
    zuordnung: TicketKundenZuordnung,
    kontext: TicketKundenKontext | null,
    actor: TicketActor
  ): Promise<TicketOperationResult> {
    try {
      const neuerKunde = zuordnung.customer_id;
      const neueGruppe = neuerKunde ? null : zuordnung.customer_group_id;

      const { data: current, error: loadError } = await supabase
        .from('tickets')
        .select('customer_id, customer_group_id')
        .eq('id', ticketId)
        .maybeSingle();

      if (loadError || !current) {
        return { success: false, error: 'Ticket nicht gefunden' };
      }

      const alterKunde = (current.customer_id as string | null) ?? null;
      const alteGruppe = (current.customer_group_id as string | null) ?? null;

      if (alterKunde !== neuerKunde || alteGruppe !== neueGruppe) {
        const { error: updateError } = await supabase
          .from('tickets')
          .update({ customer_id: neuerKunde, customer_group_id: neueGruppe })
          .eq('id', ticketId);

        if (updateError) {
          console.error('[TicketService.updateCustomerAssignment] Update-Error:', updateError);
          return { success: false, error: updateError.message };
        }

        await this.logEvent({
          ticket_id: ticketId,
          event_type: 'customer_changed',
          from_value: alterKunde,
          to_value: neuerKunde,
          actor,
          metadata: {
            from_customer_group_id: alteGruppe,
            to_customer_group_id: neueGruppe,
            regel: neuerKunde ? zuordnung.regel : 'keine'
          }
        });
      }

      // Absender-Regel fuer kuenftige Mails (nur mit Kunde)
      if (neuerKunde && zuordnung.regel !== 'keine') {
        const wert = zuordnung.regel === 'adresse' ? kontext?.absender : kontext?.domain;
        const waehlbar = zuordnung.regel === 'adresse'
          ? kontext?.adresse_waehlbar === true
          : kontext?.domain_waehlbar === true;

        if (!wert || !waehlbar) {
          return {
            success: false,
            error: 'Die Zuordnung wurde gespeichert, die Regel für künftige Mails ist für diesen Absender aber nicht möglich.'
          };
        }

        const { error: ruleError } = await supabase
          .rpc('set_ticket_absender_regel', {
            p_art: zuordnung.regel,
            p_wert: wert,
            p_customer_id: neuerKunde,
            p_staff_id: actor.staff_id,
            p_staff_email: actor.staff_email,
            p_quelle: 'ticket'
          });

        if (ruleError) {
          console.error('[TicketService.updateCustomerAssignment] Regel-Error:', ruleError);
          return {
            success: false,
            error: `Die Zuordnung wurde gespeichert, die Regel für künftige Mails aber nicht: ${ruleError.message}`
          };
        }
      }

      return { success: true };
    } catch (error) {
      console.error('[TicketService.updateCustomerAssignment] Unexpected error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }

  /**
   * Zuordnungsregeln eines Kunden (Absenderadresse bzw. Domain -> Kunde),
   * sortiert nach Art und Wert. Leere Liste bei Fehler.
   */
  async listSenderRules(customerId: string): Promise<TicketAbsenderRegel[]> {
    try {
      const { data, error } = await supabase
        .from('ticket_absender_regeln')
        .select('id, art, wert, customer_id, angelegt_von_email, quelle, created_at')
        .eq('customer_id', customerId)
        .order('art', { ascending: true })
        .order('wert', { ascending: true })
        .limit(1000);

      if (error) {
        console.error('[TicketService.listSenderRules] Load-Error:', error);
        return [];
      }
      return (data ?? []) as TicketAbsenderRegel[];
    } catch (error) {
      console.error('[TicketService.listSenderRules] Unexpected error:', error);
      return [];
    }
  }

  /**
   * Legt eine Zuordnungsregel an bzw. stellt eine bestehende Regel fuer
   * denselben Wert auf diesen Kunden um (RPC set_ticket_absender_regel mit
   * Validierung: keine eigene Domain, keine Domains oeffentlicher Anbieter).
   */
  async createSenderRule(
    art: 'adresse' | 'domain',
    wert: string,
    customerId: string,
    actor: TicketActor,
    quelle: 'ticket' | 'stammdaten'
  ): Promise<TicketOperationResult> {
    try {
      const { error } = await supabase
        .rpc('set_ticket_absender_regel', {
          p_art: art,
          p_wert: wert,
          p_customer_id: customerId,
          p_staff_id: actor.staff_id,
          p_staff_email: actor.staff_email,
          p_quelle: quelle
        });

      if (error) {
        console.error('[TicketService.createSenderRule] RPC-Error:', error);
        return { success: false, error: error.message };
      }
      return { success: true };
    } catch (error) {
      console.error('[TicketService.createSenderRule] Unexpected error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }

  /** Loescht eine Zuordnungsregel (RPC delete_ticket_absender_regel). */
  async deleteSenderRule(ruleId: string): Promise<TicketOperationResult> {
    try {
      const { data, error } = await supabase
        .rpc('delete_ticket_absender_regel', { p_id: ruleId });

      if (error) {
        console.error('[TicketService.deleteSenderRule] RPC-Error:', error);
        return { success: false, error: error.message };
      }
      if (data !== true) {
        return { success: false, error: 'Die Regel wurde nicht gefunden.' };
      }
      return { success: true };
    } catch (error) {
      console.error('[TicketService.deleteSenderRule] Unexpected error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }

  // ===========================================================================
  // SCHEDULED MESSAGES (F3 — Send-later)
  // ===========================================================================

  /**
   * Plant eine Antwort an einem bestehenden Ticket. Es wird KEINE Nachricht und
   * KEIN Mailversand erzeugt — nur ein Eintrag in ticket_scheduled_messages plus
   * die Anhaenge unter scheduled/<id>/. Der Cron (ticket-scheduled-send) holt den
   * Eintrag zum Zeitpunkt ab, materialisiert die Nachricht und versendet sie.
   */
  async scheduleReply(input: ScheduleReplyInput, actor: TicketActor): Promise<ScheduleResult> {
    try {
      if (!input.to_emails || input.to_emails.length === 0) {
        return { success: false, error: 'Empfänger-Adresse erforderlich.' };
      }

      let scheduledIso: string;
      try {
        scheduledIso = this.berlinLocalToUtcIso(input.scheduled_at_local);
      } catch (e) {
        return { success: false, error: e instanceof Error ? e.message : 'Ungültiger Zeitpunkt' };
      }
      if (new Date(scheduledIso).getTime() <= Date.now()) {
        return { success: false, error: 'Der geplante Zeitpunkt liegt in der Vergangenheit.' };
      }

      // Aussenidentitaet (sent_as_*) nur gesetzt, wenn "Senden als andere Person"
      // gewaehlt wurde; created_by_* bleibt die echte Person (Audit). materialize
      // uebernimmt die sent_as_*-Felder spaeter auf die erzeugte Nachricht.
      const { data, error } = await supabase
        .from('ticket_scheduled_messages')
        .insert({
          ticket_id: input.ticket_id,
          body_text: input.body_text,
          body_html: input.body_html ?? null,
          to_emails: input.to_emails,
          cc_emails: input.cc_emails ?? [],
          scheduled_at: scheduledIso,
          status: 'pending',
          created_by_staff_id: actor.staff_id,
          created_by_email: actor.staff_email,
          created_by_name: actor.staff_full_name ?? null,
          sent_as_staff_id: input.sent_as?.staff_id ?? null,
          sent_as_name: input.sent_as?.name ?? null
        })
        .select('id')
        .single();

      if (error || !data) {
        console.error('[TicketService.scheduleReply] Insert-Error:', error);
        return { success: false, error: error?.message ?? 'Geplante Nachricht konnte nicht angelegt werden' };
      }

      const scheduledId = data.id as string;

      if (
        (input.attachments && input.attachments.length > 0) ||
        (input.inline_images && input.inline_images.length > 0)
      ) {
        await this.attachToScheduledMessage(scheduledId, [
          ...(input.attachments ?? []),
          ...(input.inline_images ?? [])
        ]);
      }

      await this.logEvent({
        ticket_id: input.ticket_id,
        event_type: 'message_scheduled',
        from_value: null,
        to_value: scheduledIso,
        actor,
        metadata: {
          scheduled_message_id: scheduledId,
          to_emails: input.to_emails,
          ...(input.sent_as ? { sent_as_staff_id: input.sent_as.staff_id, sent_as_name: input.sent_as.name } : {})
        }
      });

      return { success: true, scheduled_id: scheduledId };
    } catch (error) {
      console.error('[TicketService.scheduleReply] Unexpected error:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unbekannter Fehler' };
    }
  }

  /**
   * Legt ein neues Ticket SOFORT an (taucht in der Liste auf, ist zuweisbar) und
   * terminiert nur den Versand der ersten Nachricht. Es wird jetzt keine
   * ticket_messages-Zeile erzeugt — die entsteht beim Versand via materialize.
   */
  async scheduleNewTicket(input: ScheduleNewTicketInput, actor: TicketActor): Promise<ScheduleNewTicketResult> {
    try {
      if (!input.to_emails || input.to_emails.length === 0) {
        return { success: false, error: 'Empfänger-Adresse erforderlich.' };
      }

      let scheduledIso: string;
      try {
        scheduledIso = this.berlinLocalToUtcIso(input.scheduled_at_local);
      } catch (e) {
        return { success: false, error: e instanceof Error ? e.message : 'Ungültiger Zeitpunkt' };
      }
      if (new Date(scheduledIso).getTime() <= Date.now()) {
        return { success: false, error: 'Der geplante Zeitpunkt liegt in der Vergangenheit.' };
      }

      // 1. Ticket sofort anlegen (Status open, wie createTicket). Keine erste
      //    Nachricht — die kommt beim Versand.
      const { data: ticketData, error: ticketError } = await supabase
        .from('tickets')
        .insert({
          customer_id: input.customer_id,
          subject: input.subject,
          status: 'open',
          priority: input.priority ?? 'normal',
          assigned_to_staff_id: input.assigned_to_staff_id ?? actor.staff_id,
          source: 'intranet',
          created_by_staff_id: actor.staff_id,
          last_message_at: new Date().toISOString(),
          last_message_from: null
        })
        .select('*')
        .single();

      if (ticketError || !ticketData) {
        console.error('[TicketService.scheduleNewTicket] Ticket-Insert-Error:', ticketError);
        return { success: false, error: ticketError?.message ?? 'Ticket-Anlage fehlgeschlagen' };
      }
      const ticket = ticketData as Ticket;

      // 2. Geplante Erst-Nachricht. Aussenidentitaet analog scheduleReply.
      const { data: schedData, error: schedError } = await supabase
        .from('ticket_scheduled_messages')
        .insert({
          ticket_id: ticket.id,
          body_text: input.body_text,
          body_html: input.body_html ?? null,
          to_emails: input.to_emails,
          cc_emails: input.cc_emails ?? [],
          scheduled_at: scheduledIso,
          status: 'pending',
          created_by_staff_id: actor.staff_id,
          created_by_email: actor.staff_email,
          created_by_name: actor.staff_full_name ?? null,
          sent_as_staff_id: input.sent_as?.staff_id ?? null,
          sent_as_name: input.sent_as?.name ?? null
        })
        .select('id')
        .single();

      if (schedError || !schedData) {
        console.error('[TicketService.scheduleNewTicket] Scheduled-Insert-Error:', schedError);
        return { success: false, ticket, error: 'Ticket angelegt, aber geplante Nachricht konnte nicht erstellt werden' };
      }
      const scheduledId = schedData.id as string;

      if (
        (input.attachments && input.attachments.length > 0) ||
        (input.inline_images && input.inline_images.length > 0)
      ) {
        await this.attachToScheduledMessage(scheduledId, [
          ...(input.attachments ?? []),
          ...(input.inline_images ?? [])
        ]);
      }

      await this.logEvent({
        ticket_id: ticket.id,
        event_type: 'created',
        from_value: null,
        to_value: ticket.ticket_number,
        actor,
        metadata: {
          subject: ticket.subject,
          customer_id: ticket.customer_id,
          assigned_to: ticket.assigned_to_staff_id,
          scheduled: true
        }
      });
      await this.logEvent({
        ticket_id: ticket.id,
        event_type: 'message_scheduled',
        from_value: null,
        to_value: scheduledIso,
        actor,
        metadata: {
          scheduled_message_id: scheduledId,
          to_emails: input.to_emails,
          ...(input.sent_as ? { sent_as_staff_id: input.sent_as.staff_id, sent_as_name: input.sent_as.name } : {})
        }
      });

      return { success: true, ticket, scheduled_id: scheduledId };
    } catch (error) {
      console.error('[TicketService.scheduleNewTicket] Unexpected error:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unbekannter Fehler' };
    }
  }

  /**
   * Bricht eine geplante Sendung ab. Greift nur, solange sie noch 'pending' ist —
   * sobald der Cron sie geclaimt hat ('sending') oder sie raus ist, schlaegt es
   * fehl und es wird false zurueckgegeben.
   */
  async cancelScheduledMessage(scheduledId: string, actor: TicketActor): Promise<TicketOperationResult> {
    try {
      const { data, error } = await supabase
        .from('ticket_scheduled_messages')
        .update({ status: 'cancelled' })
        .eq('id', scheduledId)
        .eq('status', 'pending')
        .select('id, ticket_id')
        .maybeSingle();

      if (error) {
        console.error('[TicketService.cancelScheduledMessage] Update-Error:', error);
        return { success: false, error: error.message };
      }
      if (!data) {
        return { success: false, error: 'Versand läuft bereits oder ist abgeschlossen — Abbrechen nicht mehr möglich.' };
      }

      await this.logEvent({
        ticket_id: data.ticket_id,
        event_type: 'scheduled_cancelled',
        from_value: null,
        to_value: null,
        actor,
        metadata: { scheduled_message_id: scheduledId }
      });

      return { success: true };
    } catch (error) {
      console.error('[TicketService.cancelScheduledMessage] Unexpected error:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unbekannter Fehler' };
    }
  }

  /**
   * Laedt die Anhaenge einer geplanten Sendung in den scheduled/<id>/-Prefix des
   * ticket-attachments-Buckets und legt die Metadaten-Zeilen an. Spiegelt
   * attachToMessage, schreibt aber nach ticket_scheduled_message_attachments.
   * Best-effort: einzelne Fehler werden geloggt, nicht geworfen.
   */
  private async attachToScheduledMessage(
    scheduledId: string,
    attachments: TicketAttachmentInput[]
  ): Promise<void> {
    if (!attachments || attachments.length === 0) return;

    for (let i = 0; i < attachments.length; i++) {
      const att = attachments[i];
      if (!att || !att.filename || !att.content_base64) {
        console.warn('[TicketService.attachToScheduledMessage] Anhang uebersprungen (filename oder content fehlt)');
        continue;
      }

      const isInline = att.is_inline === true;
      const storagePath = isInline
        ? `scheduled/${scheduledId}/inline/${i}-${att.filename}`
        : `scheduled/${scheduledId}/${att.filename}`;

      try {
        const byteCharacters = atob(att.content_base64);
        const byteArray = new Uint8Array(byteCharacters.length);
        for (let j = 0; j < byteCharacters.length; j++) {
          byteArray[j] = byteCharacters.charCodeAt(j);
        }
        const blob = new Blob([byteArray], {
          type: att.content_type || 'application/octet-stream'
        });

        const { error: uploadError } = await supabase.storage
          .from('ticket-attachments')
          .upload(storagePath, blob, {
            contentType: att.content_type || 'application/octet-stream',
            upsert: true
          });

        if (uploadError) {
          console.warn(`[TicketService.attachToScheduledMessage] Storage-Upload fehlgeschlagen fuer ${att.filename}:`, uploadError.message);
          continue;
        }

        const { error: insertError } = await supabase
          .from('ticket_scheduled_message_attachments')
          .insert({
            scheduled_message_id: scheduledId,
            storage_path: storagePath,
            filename: att.filename,
            content_type: att.content_type || 'application/octet-stream',
            file_size: att.file_size || byteArray.length,
            is_inline: isInline,
            content_id: att.content_id ?? null
          });

        if (insertError) {
          console.warn(`[TicketService.attachToScheduledMessage] Insert fehlgeschlagen fuer ${att.filename}:`, insertError.message);
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.warn(`[TicketService.attachToScheduledMessage] Unerwarteter Fehler bei Anhang ${att.filename}:`, msg);
      }
    }
  }

  /**
   * Interpretiert eine Wanduhrzeit ("YYYY-MM-DDTHH:mm") fest als Europe/Berlin und
   * gibt den entsprechenden UTC-Zeitpunkt als ISO-String zurueck. DST-korrekt ueber
   * Intl. Die seltene Doppel-/Luecken-Stunde an der Umstellung wird nicht gesondert
   * behandelt (akzeptabler Randfall fuer Terminierung).
   */
  private berlinLocalToUtcIso(local: string): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local);
    if (!match) {
      throw new Error('Ungültiges Datums-/Zeitformat');
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const hour = Number(match[4]);
    const minute = Number(match[5]);

    const utcGuess = Date.UTC(year, month - 1, day, hour, minute);
    const offsetMinutes = this.berlinOffsetMinutes(new Date(utcGuess));
    return new Date(utcGuess - offsetMinutes * 60000).toISOString();
  }

  /** Offset von Europe/Berlin gegenueber UTC (in Minuten) zum gegebenen Zeitpunkt. */
  private berlinOffsetMinutes(date: Date): number {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/Berlin',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    const map: Record<string, number> = {};
    for (const part of dtf.formatToParts(date)) {
      if (part.type !== 'literal') map[part.type] = Number(part.value);
    }
    const asUtc = Date.UTC(map.year, map.month - 1, map.day, map.hour, map.minute, map.second);
    return Math.round((asUtc - date.getTime()) / 60000);
  }

  // ===========================================================================
  // PRIVATE HELPER
  // ===========================================================================

  /**
   * Schreibt ein Audit-Event. Best-effort: bei Fehler nur Log, kein Throw,
   * damit die eigentliche Operation nicht blockiert wird.
   */
  private async logEvent(params: {
    ticket_id: string;
    event_type: TicketEventType;
    from_value: string | null;
    to_value: string | null;
    actor: TicketActor;
    metadata: Record<string, unknown>;
  }): Promise<void> {
    try {
      const { error } = await supabase
        .from('ticket_events')
        .insert({
          ticket_id: params.ticket_id,
          event_type: params.event_type,
          from_value: params.from_value,
          to_value: params.to_value,
          actor_staff_id: params.actor.staff_id,
          actor_email: params.actor.staff_email,
          metadata: params.metadata
        });

      if (error) {
        console.error('[TicketService.logEvent] Event-Log-Error:', error);
      }
    } catch (error) {
      console.error('[TicketService.logEvent] Unexpected error:', error);
    }
  }

  /**
   * Holt Message-Counts fuer eine Liste von Ticket-IDs als Map.
   * PostgREST hat keine eingebaute COUNT-Aggregation pro Gruppe, daher laden
   * wir ticket_messages.ticket_id fuer die Liste und gruppieren clientseitig.
   */
  private async fetchMessageCounts(ticketIds: string[]): Promise<Record<string, number>> {
    if (ticketIds.length === 0) return {};

    try {
      const { data, error } = await supabase
        .from('ticket_messages')
        .select('ticket_id')
        .in('ticket_id', ticketIds)
        .eq('is_internal_note', false)
        .limit(100000);

      if (error) {
        console.error('[TicketService.fetchMessageCounts] Error:', error);
        return {};
      }

      const counts: Record<string, number> = {};
      const rows = (data ?? []) as Array<{ ticket_id: string }>;
      for (const row of rows) {
        counts[row.ticket_id] = (counts[row.ticket_id] ?? 0) + 1;
      }
      return counts;
    } catch (error) {
      console.error('[TicketService.fetchMessageCounts] Unexpected error:', error);
      return {};
    }
  }

  private mapRawToListView(raw: RawTicketWithJoins, messageCount: number): TicketListView {
    return {
      id: raw.id,
      ticket_number: raw.ticket_number,
      customer_id: raw.customer_id,
      subject: raw.subject,
      status: raw.status,
      priority: raw.priority,
      assigned_to_staff_id: raw.assigned_to_staff_id,
      source: raw.source,
      gmail_thread_id: raw.gmail_thread_id,
      created_by_staff_id: raw.created_by_staff_id,
      last_message_at: raw.last_message_at,
      last_message_from: raw.last_message_from,
      created_at: raw.created_at,
      updated_at: raw.updated_at,
      closed_at: raw.closed_at,
      customer_group_id: raw.customer_group_id ?? null,
      customer: raw.customer,
      customer_group: raw.customer_group ?? null,
      assigned_to: raw.assigned_to,
      message_count: messageCount
    };
  }
}

// Singleton-Export
export const ticketService = new TicketService();

// =============================================================================
// Inline-Bilder: cid:-Referenzen im Mail-HTML aufloesen
// =============================================================================

/**
 * Ersetzt im Mail-HTML die `src="cid:<content-id>"`-Referenzen durch die
 * signierte Storage-URL des passenden Inline-Bilds. Nicht aufgeloeste cid:-
 * Referenzen bleiben stehen und werden in der Anzeige (TicketDetail) entfernt.
 *
 * Schluessel ist die content_id (ohne spitze Klammern). Beruehrt nur img-src,
 * laesst sonstiges HTML unveraendert. Die Ersetzung nutzt eine Funktion, damit
 * Sonderzeichen ($) in der signierten URL nicht als Backreference interpretiert
 * werden.
 */
function rewriteCidReferences(
  html: string | null,
  inlineAttachments: TicketAttachment[] | undefined,
  signedUrlByPath: Record<string, string>
): string | null {
  if (!html) return html;
  if (!inlineAttachments || inlineAttachments.length === 0) return html;

  let result = html;
  for (const att of inlineAttachments) {
    if (!att.content_id) continue;
    const url = signedUrlByPath[att.storage_path];
    if (!url) continue;
    const cid = att.content_id.replace(/^<+|>+$/g, '').trim();
    if (!cid) continue;
    const escaped = escapeRegExp(cid);
    // Erfasst quotierte (src="cid:..." / src='cid:...') UND unquotierte
    // (src=cid:..., wie von IBM/HCL Domino erzeugt) Attribute. Die unquotierte
    // Form endet an Leerzeichen, '/' oder '>'. Ausgabe stets als sauber
    // quotiertes src="<signierte URL>".
    const pattern = new RegExp(
      `src\\s*=\\s*(?:"cid:${escaped}"|'cid:${escaped}'|cid:${escaped}(?=[\\s/>]))`,
      'gi'
    );
    result = result.replace(pattern, () => `src="${url}"`);
  }
  return result;
}

/** Maskiert Regex-Sonderzeichen fuer die Verwendung in einem RegExp-Konstruktor. */
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Minimaler HTML->Text-Helfer fuer den Verlauf der KI-Pruefung (Fallback, wenn
 * body_text fehlt). Block-Elemente und <br> werden zu Zeilenumbruechen; gaengige
 * HTML-Entities werden dekodiert. Bewusst leichtgewichtig (kein DOMParser noetig).
 */
function stripHtmlToPlainText(html: string): string {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|blockquote|h[1-6])>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/[ \t]+/g, ' ')
    .replace(/\s*\n\s*/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// =============================================================================
// Anrede/Begruessung fuer die KI-Pruefung
// =============================================================================

/**
 * Person, deren Anrede gebildet werden soll (Kontakt oder Kunde). Die Felder
 * sind bewusst optional und locker typisiert, da sie aus unterschiedlichen
 * Tabellen stammen (customer_contacts bzw. customers).
 */
interface SalutationPerson {
  anrede?: string | null;
  geschlecht?: string | null;
  vorname?: string | null;
  nachname?: string | null;
}

/**
 * Bildet die vorgegebene Begrüßung ("Guten Tag …,") deterministisch aus den
 * Stammdaten. Die KI übernimmt sie unverändert und leitet selbst kein
 * Geschlecht ab. Regeln:
 *   - "Sie" + männlich → "Guten Tag Herr <Nachname>,"
 *   - "Sie" + weiblich → "Guten Tag Frau <Nachname>,"
 *   - "Du"             → "Guten Tag <Vorname>,"
 *   - alles Übrige     → "Guten Tag,"
 *
 * Ist das Geschlecht nicht aus den Stammdaten belegt, wird BEWUSST ohne Namen
 * gegrüßt. Die frühere namentliche Notlösung ("Guten Tag Heike Schumann-Müller,")
 * ist in der Außenwirkung unprofessionell und wurde ersatzlos gestrichen: eine
 * Begrüßung ohne Namen ist immer korrekt, eine mit vollem Vor- und Nachnamen
 * nie. Kennt der Bearbeiter den Empfänger und schreibt selbst "Frau <Nachname>",
 * bleibt das stehen; darum kümmert sich die KI-Prüfung serverseitig.
 *
 * Liefert stets einen nicht-leeren String.
 */
function buildSalutation(person: SalutationPerson | null): string {
  if (person) {
    const istDu = (person.anrede || '').trim().toLowerCase() === 'du';
    const vorname = (person.vorname || '').trim();
    const nachname = (person.nachname || '').trim();
    const geschlecht = (person.geschlecht || '').trim().toLowerCase();

    if (istDu && vorname) {
      return `Guten Tag ${vorname},`;
    }
    if (!istDu) {
      if (geschlecht === 'männlich' && nachname) {
        return `Guten Tag Herr ${nachname},`;
      }
      if (geschlecht === 'weiblich' && nachname) {
        return `Guten Tag Frau ${nachname},`;
      }
    }
  }

  return 'Guten Tag,';
}
